<template>
  <div class="d-flex flex-row gap-3 p-2">
    <DocumentCard
      :title="$t('facturas.modal_detalle.documento_factura')"
      :actions="['open', 'download']"
      :initialDocumentList="documentoFactura ? [documentoFactura] : []"
    />
    <DocumentCard
      :title="$t('facturas.modal_detalle.documento_factura_propia')"
      :actions="accionesFacturaPropia"
      :max-files=1
      :multiple=false
      :initialDocumentList="listaInicialFacturaPropia"
      @added:document="onFacturaPropiaSubida"
      @updated:document="onFacturaPropiaSubida"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FacturaDetailModel } from "@api/api";
import { useApi } from "@api/integration";
import DocumentCard from "@/.shared/components/document-card/DocumentCard.vue";
import type { IDocumentCardProps, IDocumentInitialState } from "@/.shared/components/document-card/models/IDocumentCardProps";
import type { CardFile } from "@/.shared/components/document-card/models/CardFile";
import { getDocumentalFileName } from "@/.shared/helpers/documental-file-name-helper";
import { useGrupoUsuarioChecker } from "@/.shared/services/auth/authorization/grupo-usuario-checker";
import { useIntl } from "@/.shared/services/i18n";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";

const props = defineProps<{
  idFactura: string;
  model: FacturaDetailModel | null;
}>();

const emit = defineEmits<{
  "updated-factura": [];
}>();

const api = useApi();
const intl = useIntl();
const { esAgente } = useGrupoUsuarioChecker();

const puedeSubirFacturaPropia = computed(() => esAgente());

const documentoFactura = computed<IDocumentInitialState | null>(() => {
  if (!props.model?.RutaDescarga) return null;
  return {
    id: `factura-${props.idFactura}`,
    uri: props.model.RutaDescarga,
    fileName: getDocumentalFileName(props.model.RutaDescarga)
  };
});

const documentoFacturaPropia = computed<IDocumentInitialState | null>(() => {
  if (!props.model?.RutaFacturaPropia) return null;
  return {
    id: `factura-propia-${props.idFactura}`,
    uri: props.model.RutaFacturaPropia,
    fileName: getDocumentalFileName(props.model.RutaFacturaPropia)
  };
});

const accionesFacturaPropia = computed((): NonNullable<IDocumentCardProps["actions"]> =>
  puedeSubirFacturaPropia.value
    ? ["open", "download", "replace", "add"]
    : ["open", "download"],
);

const listaInicialFacturaPropia = computed<IDocumentInitialState[]>(() =>
  documentoFacturaPropia.value ? [documentoFacturaPropia.value] : [],
);

async function onFacturaPropiaSubida(document: CardFile): Promise<void> {
  const file = document.file;
  if (!file) return;

  try {
    await api.callStandardApi("putFacturaPropia", { showLoading: true }, props.idFactura, file.name, file);
    toastSuccess(intl.i18n.t("facturas.grid.factura_propia_subida"));
    emit("updated-factura");
  } catch {
    toastError(intl.i18n.t("datos_factura.errores.factura_propia"));
  }
}
</script>
